import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

import { environment } from '@environment/environment';
import { APIResponseModel } from '@models/common.model';
import { ActivePlan, PlansResponse, PricingPlan } from '@models/pricing.model';
import { UserStateService } from '@services/user-state.service';

@Injectable({
  providedIn: 'root'
})
export class PricingService {
  private cacheTimestamp = 0; // 2 mins
  private readonly cacheValidityPeriod = 2 * 60 * 1000;
  private readonly pricingBaseUrl = environment.pricingApiUrl;
  private readonly http = inject(HttpClient);
  private readonly userStateService = inject(UserStateService);

  // Cache the active plan to avoid multiple API calls
  private cachedActivePlan: Observable<
    APIResponseModel<ActivePlan | null>
  > | null = null;

  /**
   * Fetches the active pricing plan for the current user
   * @param forceRefresh Whether to force a refresh of the cache
   * @returns Observable with the active plan data
   */
  getActivePlan(
    forceRefresh = false
  ): Observable<APIResponseModel<ActivePlan | null>> {
    const now = Date.now();
    const isCacheExpired = now - this.cacheTimestamp > this.cacheValidityPeriod;

    if (this.cachedActivePlan && !forceRefresh && !isCacheExpired) {
      return this.cachedActivePlan;
    }

    const userId = this.getUserId();
    if (!userId) {
      console.error('User ID not found for pricing plan retrieval');
      return of({
        status: false,
        statusCode: 400,
        message: 'User ID not found',
        data: null,
        error: null
      });
    }

    this.cacheTimestamp = now;
    this.cachedActivePlan = this.http
      .get<
        APIResponseModel<ActivePlan | null>
      >(`${this.pricingBaseUrl}/user/active/plan?user_uuid=${userId}`)
      .pipe(
        shareReplay(1) // Cache the result
      );

    return this.cachedActivePlan;
  }

  /**
   * Get the remaining credentials count from the active plan
   * @returns Observable with the remaining credentials count
   */
  getRemainingCredentials(): Observable<number> {
    return this.getActivePlan().pipe(
      map((response) => {
        if (!response?.data?.remaining_features) return 0;
        return response.data.remaining_features.number_of_credentials;
      })
    );
  }

  /**
   * Get the maximum credentials allowed by the plan
   * @returns Observable with the maximum credentials count
   */
  getMaxCredentials(): Observable<number> {
    return this.getActivePlan().pipe(
      map((response) => {
        if (!response?.data?.plan_json?.features) return 0;
        return response.data.plan_json.features.number_of_credentials;
      })
    );
  }

  /**
   * Get the plan name
   * @returns Observable with the plan name
   */
  getPlanName(): Observable<string> {
    return this.getActivePlan().pipe(
      map((response) => {
        if (!response?.data?.plan_json) return '';
        return response.data.plan_json.name;
      })
    );
  }

  /**
   * Fetches all available pricing plans
   * @returns Observable with array of pricing plans
   */
  getAvailablePlans(): Observable<PricingPlan[]> {
    return this.http.get<PlansResponse>(`${this.pricingBaseUrl}/plans`).pipe(
      map((response) => response.data.list),
      catchError((error) => {
        console.error('Failed to fetch available plans:', error);
        return of([]);
      })
    );
  }

  /**
   * Upgrades the user to a new plan
   * @param planId The ID of the plan to upgrade to
   * @returns Observable with the upgrade result
   */
  upgradeToPlan(planId: string) {
    const userId = this.getUserId();
    if (!userId) {
      console.error('User ID not found for plan upgrade');
      return of({ success: false, message: 'User ID not found' });
    }

    return this.http
      .post(`${this.pricingBaseUrl}/user/upgrade`, {
        user_uuid: userId,
        plan_id: planId
      })
      .pipe(
        map((response) => {
          // Clear the cached plan after upgrade
          this.cachedActivePlan = null;
          return response;
        }),
        catchError((error) => {
          console.error('Failed to upgrade plan:', error);
          return of({ success: false, message: 'Upgrade failed' });
        })
      );
  }

  /**
   * Checks if the user has enough credentials for an operation
   * @param requiredCredentials Number of credentials required
   * @returns Observable boolean indicating if user has enough credentials
   */
  hasEnoughCredentials(requiredCredentials: number): Observable<boolean> {
    return this.getRemainingCredentials().pipe(
      map((remainingCredentials) => remainingCredentials >= requiredCredentials)
    );
  }

  /**
   * Navigates to the upgrade/plans page for the user
   * @returns The route to the plans page
   */
  getUpgradeRoute(): string {
    return '/admin/plans';
  }

  /**
   * Refreshes the pricing plan data
   * This should be called after operations that affect credential count
   */
  refreshPlanData(): void {
    this.cachedActivePlan = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Gets the pricing plan UUID from storage
   * @returns The user ID or null if not found
   */
  private getUserId(): string | null {
    return this.userStateService.getUserData()()?.pricing_plan_uuid || null;
  }
}
